import { Injectable } from '@angular/core';
import { NotificationsService } from 'api/notifications.api';
import { GetTicketsResponse, GetAllTicketsResponse, TicketingApiService } from 'api/ticketing.api';
import { Observable, Subject } from 'rxjs';
import { auditTime, exhaustMap, filter, publishReplay, refCount, startWith, switchMap } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class TicketsService {
    private _ticketsObservableCache: { [stationId: string]: Observable<GetTicketsResponse> } = {};
    private _ticketsRefreshRequest$: { [stationId: string]: Subject<void> } = {};

    constructor(
        private ticketingApi: TicketingApiService,
        private notifications: NotificationsService
    ) {}

    ticketsRefreshRequest$(stationId: string) {
        if (!this._ticketsRefreshRequest$[stationId]) {
            this._ticketsRefreshRequest$[stationId] = new Subject<void>();
            this.notifications.notification$
                .pipe(
                    filter(
                        (n) =>
                            n.type === 'ChargingStationUpdated' &&
                            n.changedSchemas?.includes('ticket') &&
                            n.stationId === stationId
                    )
                )
                .subscribe(() => {
                    this._ticketsRefreshRequest$[stationId].next();
                });
        }
        return this._ticketsRefreshRequest$[stationId];
    }

    allOpenTickets$(): Observable<GetAllTicketsResponse> {
        const updated$ = this.notifications.notification$.pipe(
            filter((n) => n.type === 'ChargingStationUpdated' && n.changedSchemas?.includes('ticket'))
        );
        return updated$.pipe(
            startWith([]),
            switchMap(() => this.ticketingApi.getAllTickets({ status: 'OPEN' }))
        );
    }

    allTickets$(): Observable<GetAllTicketsResponse> {
        const updated$ = this.notifications.notification$.pipe(
            filter((n) => n.type === 'ChargingStationUpdated' && n.changedSchemas?.includes('ticket'))
        );
        return updated$.pipe(
            startWith([]),
            switchMap(() => this.ticketingApi.getAllTickets())
        );
    }

    tickets$(stationId: string): Observable<GetTicketsResponse> {
        if (!this._ticketsObservableCache[stationId]) {
            this._ticketsObservableCache[stationId] = this.ticketsRefreshRequest$(stationId).pipe(
                auditTime(0),
                startWith([]),
                exhaustMap(() =>
                    this.ticketingApi.getTickets(stationId).catch((e) => {
                        console.error('tickets$', e);
                        return Promise.resolve<GetTicketsResponse>(undefined);
                    })
                ),
                filter((res) => !!res),
                publishReplay(1),
                refCount()
            );
        }
        return this._ticketsObservableCache[stationId];
    }
}
